import React, { useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { API } from '../utils/api';
import {
  ShieldCheck, Lock, FileSearch, Megaphone, Upload, Trash2, AlertTriangle,
  Eye, EyeOff, MessageSquare, Phone, Mail, Building2, CheckCircle2, Loader2,
  Camera, Video, Mic, FileText, ChevronDown,
} from 'lucide-react';

const MAX_FILES = 5;
const MAX_FILE_MB = 25;
const ACCEPT = 'image/*,video/*,audio/*';

const FAQ = [
  {
    q: '¿Mi denuncia es confidencial?',
    a: 'Sí. Tu identidad nunca se publica sin tu autorización explícita. Si decidimos publicar un caso verificado, lo hacemos protegiendo tus datos personales. Tu información sólo la ve el equipo editorial de SinAdicciones.org.',
  },
  {
    q: '¿Qué tipo de denuncias puedo reportar?',
    a: 'Maltrato físico o psicológico, encierro no consentido, condiciones insalubres, suplantación de profesionales, cobros indebidos, falta de habilitación, abusos económicos, retención de pacientes contra su voluntad, publicidad engañosa, terapias sin respaldo clínico y cualquier otra mala práctica en centros de rehabilitación.',
  },
  {
    q: '¿Qué pasa después de enviar mi denuncia?',
    a: 'Nuestro equipo verifica los antecedentes: revisa registros públicos (SENDA, SEREMI de Salud, Registro Civil), contrasta con otras denuncias del mismo centro y, si lo autorizas, te contactamos para ampliar detalles. Solo se publican casos verificados.',
  },
  {
    q: '¿Por qué recomiendan adjuntar fotos, videos o audios?',
    a: 'Las pruebas materiales (fotografías de instalaciones, audios de conversaciones, mensajes, videos) fortalecen el caso y permiten verificar con mayor precisión. Tus archivos se almacenan de forma cifrada y privada.',
  },
  {
    q: '¿Cuánto tiempo tarda la revisión?',
    a: 'Entre 5 y 15 días hábiles dependiendo de la complejidad. Si autorizas, te contactaremos por email o WhatsApp en ese plazo.',
  },
  {
    q: '¿Esto reemplaza una denuncia legal o policial?',
    a: 'No. SinAdicciones.org es un medio de periodismo independiente. Si fuiste víctima de un delito (lesiones, secuestro, abuso) debes denunciarlo en Fiscalía (8000 800 432), Carabineros (133) o PDI (134). Nuestro trabajo es complementario: visibilizar el problema y presionar por regulación.',
  },
  {
    q: '¿Cuesta algo denunciar?',
    a: 'No. Es 100% gratuito. SinAdicciones.org se financia mediante el directorio de centros verificados y no recibe pagos por silenciar denuncias.',
  },
];

export default function DenunciasLandingPage() {
  const fileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [form, setForm] = useState({
    nombre: '', email: '', telefono: '', centro: '', ciudad: '', region: '',
    descripcion: '', autoriza_contacto: true,
  });
  const [files, setFiles] = useState([]);

  function update(field, value) {
    setForm(function (prev) { return Object.assign({}, prev, { [field]: value }); });
  }

  function onPickFiles(e) {
    const picked = Array.from(e.target.files || []);
    const next = [];
    const total = files.length;
    for (var i = 0; i < picked.length; i += 1) {
      if (total + next.length >= MAX_FILES) {
        toast.error('Máximo ' + MAX_FILES + ' archivos por denuncia');
        break;
      }
      const f = picked[i];
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(f.name + ' supera ' + MAX_FILE_MB + ' MB');
        continue;
      }
      next.push(f);
    }
    setFiles(function (prev) { return prev.concat(next); });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removeFile(idx) {
    setFiles(function (prev) { return prev.filter(function (_, i) { return i !== idx; }); });
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.nombre || form.nombre.length < 2) { toast.error('Ingresa tu nombre'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { toast.error('Email inválido'); return; }
    if (!form.centro || form.centro.length < 2) { toast.error('Indica el centro denunciado'); return; }
    if (!form.descripcion || form.descripcion.length < 30) { toast.error('Describe la situación con al menos 30 caracteres'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('nombre', form.nombre);
      fd.append('email', form.email);
      fd.append('telefono', form.telefono || '');
      fd.append('centro', form.centro);
      fd.append('ciudad', form.ciudad || '');
      fd.append('region', form.region || '');
      fd.append('descripcion', form.descripcion);
      fd.append('autoriza_contacto', String(form.autoriza_contacto));
      files.forEach(function (f) { fd.append('files', f); });
      const res = await axios.post(API + '/denuncias/submit', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      setDone(res.data);
      // GA4 dataLayer push — denuncia submitted (lead generation)
      try {
        const a = await import('../services/analytics');
        a.trackEvent('denuncia_submitted', 'denuncia', form.centro);
      } catch (e) { /* noop */ }
      toast.success('Tu denuncia fue recibida. Revisa tu correo.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const detail = err && err.response && err.response.data && err.response.data.detail;
      toast.error(detail || 'No pudimos enviar la denuncia. Reintenta en unos segundos.');
    }
    setSubmitting(false);
  }

  const jsonLd = useMemo(function () {
    const faqLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map(function (item) {
        return { '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } };
      }),
    };
    const breadcrumbLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://sinadicciones.org/' },
        { '@type': 'ListItem', position: 2, name: 'Denuncias y reclamos contra centros de rehabilitación', item: 'https://sinadicciones.org/denuncias-centros-rehabilitacion-chile' },
      ],
    };
    const pageLd = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Denuncias y Reclamos contra Centros de Rehabilitación en Chile',
      description: 'Formulario confidencial para denunciar malas prácticas, abusos, irregularidades o reclamos contra centros de rehabilitación de drogas y alcohol en Chile. Revisado por equipo editorial. Adjunta fotos, videos y audios como evidencia.',
      url: 'https://sinadicciones.org/denuncias-centros-rehabilitacion-chile',
      inLanguage: 'es-CL',
      isPartOf: { '@type': 'WebSite', name: 'SinAdicciones.org', url: 'https://sinadicciones.org/' },
      publisher: { '@type': 'Organization', name: 'SinAdicciones.org', url: 'https://sinadicciones.org/' },
      breadcrumb: breadcrumbLd,
      mainContentOfPage: { '@type': 'WebPageElement', cssSelector: '#denuncia-form' },
      potentialAction: { '@type': 'CommunicateAction', name: 'Enviar denuncia confidencial', target: 'https://sinadicciones.org/denuncias-centros-rehabilitacion-chile#denuncia-form' },
    };
    return [pageLd, breadcrumbLd, faqLd];
  }, []);

  return (
    <div className="min-h-screen bg-slate-950" data-testid="denuncias-page">
      <title>Denuncias y Reclamos contra Centros de Rehabilitación en Chile | SinAdicciones.org</title>
      <meta name="description" content="Denuncia confidencial contra centros de rehabilitación en Chile. Maltrato, encierro, abusos, cobros indebidos o reclamos. Adjunta fotos, videos y audios. Revisión periodística antes de publicar." />
      <meta name="keywords" content="denuncias centros de rehabilitación chile, reclamos centros rehabilitación, denunciar centro de rehabilitación, malas prácticas adicciones, denuncias SENDA, abuso centros rehabilitación drogas, denuncia confidencial centros adicciones, reclamo centro de rehabilitación" />
      <link rel="canonical" href="https://sinadicciones.org/denuncias-centros-rehabilitacion-chile" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Denuncias y Reclamos contra Centros de Rehabilitación en Chile" />
      <meta property="og:description" content="Formulario confidencial. Adjunta fotos, videos y audios. Revisión periodística antes de publicar." />
      <meta property="og:url" content="https://sinadicciones.org/denuncias-centros-rehabilitacion-chile" />
      <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
      <meta name="twitter:card" content="summary_large_image" />

      {jsonLd.map(function (ld, i) {
        return (
          <script key={'ld-' + i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
        );
      })}

      <Header />

      <main className="pt-24 pb-20">
        {/* HERO */}
        <section className="relative overflow-hidden border-b border-red-900/30">
          <div className="absolute inset-0 bg-gradient-to-br from-red-950/40 via-slate-950 to-slate-950" />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #dc2626 0, transparent 40%)' }} />
          <div className="relative max-w-6xl mx-auto px-4 py-16 sm:py-20">
            <nav className="text-xs text-slate-500 mb-6">
              <Link to="/" className="hover:text-emerald-400">Inicio</Link>
              <span className="mx-2">›</span>
              <span className="text-slate-400">Denuncias contra centros de rehabilitación</span>
            </nav>
            <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-1.5 rounded-full text-xs font-semibold mb-6">
              <AlertTriangle className="w-3.5 h-3.5" />
              Canal de denuncias periodísticas · Confidencial
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 leading-tight mb-6">
              Denuncias y <span className="text-red-400">reclamos</span> contra centros de rehabilitación en Chile
            </h1>
            <p className="text-lg text-slate-300 max-w-3xl leading-relaxed mb-8">
              Si fuiste víctima o testigo de malas prácticas, abusos, encierro no consentido, cobros indebidos o cualquier irregularidad en un centro de rehabilitación de drogas o alcohol en Chile, denúncialo aquí.
              Tu identidad permanece <b className="text-slate-100">confidencial</b>. Antes de publicar cualquier caso, nuestro equipo periodístico verifica la información y recopila antecedentes.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 max-w-3xl">
              {[
                { icon: Lock, label: 'Confidencial', desc: 'Tu identidad protegida' },
                { icon: FileSearch, label: 'Verificado', desc: 'Revisión periodística antes de publicar' },
                { icon: Megaphone, label: 'Visibilidad', desc: 'Para exigir regulación real' },
              ].map(function (b) {
                const Icon = b.icon;
                return (
                  <div key={b.label} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                    <Icon className="w-5 h-5 text-red-400 mb-2" />
                    <div className="text-sm font-bold text-slate-50">{b.label}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{b.desc}</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-8 flex gap-3 flex-wrap">
              <a href="#denuncia-form" data-testid="denuncias-cta-form">
                <Button className="bg-red-600 hover:bg-red-700 text-white font-bold px-7 py-6">
                  Hacer denuncia ahora
                </Button>
              </a>
              <a href="#como-funciona">
                <Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 px-7 py-6">
                  Cómo funciona el proceso
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* COMO FUNCIONA */}
        <section id="como-funciona" className="max-w-5xl mx-auto px-4 py-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-50 mb-3 text-center">El proceso paso a paso</h2>
          <p className="text-slate-400 text-center max-w-2xl mx-auto mb-10">No publicamos denuncias sin verificar. Estos son los pasos que sigue tu reporte desde que lo envías hasta que (si corresponde) se publica.</p>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { n: '1', title: 'Envío confidencial', desc: 'Completas el formulario con detalles y adjuntas evidencias (fotos, videos, audios).', icon: MessageSquare },
              { n: '2', title: 'Recopilación', desc: 'Consultamos SENDA, SEREMI, Registro Civil y otras denuncias del mismo centro.', icon: FileSearch },
              { n: '3', title: 'Contraste', desc: 'Si autorizas, te contactamos por email/WhatsApp para ampliar detalles.', icon: Phone },
              { n: '4', title: 'Publicación', desc: 'Sólo casos verificados se publican, protegiendo tu identidad si así lo deseas.', icon: ShieldCheck },
            ].map(function (s) {
              const Icon = s.icon;
              return (
                <div key={s.n} className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-7 h-7 rounded-full bg-red-500/10 text-red-300 text-xs font-bold flex items-center justify-center border border-red-500/30">{s.n}</span>
                    <Icon className="w-4 h-4 text-slate-400" />
                  </div>
                  <h3 className="text-base font-bold text-slate-50 mb-1.5">{s.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* RECOMENDACION ARCHIVOS */}
        <section className="max-w-5xl mx-auto px-4 pb-8">
          <Card className="bg-amber-500/5 border-amber-500/30">
            <CardContent className="py-6 px-6">
              <div className="flex flex-col sm:flex-row gap-5 items-start">
                <div className="shrink-0 w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                  <Camera className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-amber-200 mb-2">Recomendamos adjuntar evidencias</h3>
                  <p className="text-sm text-amber-100/80 leading-relaxed mb-3">
                    Los archivos materiales fortalecen tu denuncia y aceleran la verificación. Puedes adjuntar:
                  </p>
                  <div className="grid sm:grid-cols-3 gap-3 text-xs">
                    <div className="flex items-center gap-2 text-amber-200/90"><Camera className="w-3.5 h-3.5" /> Fotografías (instalaciones, lesiones, documentos)</div>
                    <div className="flex items-center gap-2 text-amber-200/90"><Video className="w-3.5 h-3.5" /> Videos (capturas, grabaciones in situ)</div>
                    <div className="flex items-center gap-2 text-amber-200/90"><Mic className="w-3.5 h-3.5" /> Audios (conversaciones, mensajes de voz)</div>
                  </div>
                  <p className="text-xs text-amber-100/70 mt-3">Tus archivos viajan cifrados, se guardan privados y sólo el equipo editorial accede a ellos.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* FORM */}
        <section id="denuncia-form" className="max-w-3xl mx-auto px-4 py-8 scroll-mt-24">
          {done ? (
            <Card className="bg-emerald-900/20 border-emerald-500/40" data-testid="denuncias-success-card">
              <CardContent className="py-10 px-8 text-center">
                <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-slate-50 mb-3">Recibimos tu denuncia</h2>
                <p className="text-slate-300 mb-2">{done.message}</p>
                <p className="text-xs text-slate-400 mb-6">ID de seguimiento: <code className="bg-slate-800 px-2 py-1 rounded text-emerald-300">{done.denuncia_id}</code></p>
                <p className="text-sm text-slate-400 max-w-md mx-auto mb-6">Te enviamos un correo de confirmación. Nuestro equipo iniciará la recopilación de antecedentes y, si autorizaste el contacto, te escribiremos en los próximos días.</p>
                <Link to="/"><Button variant="outline" className="border-slate-700">Volver al inicio</Button></Link>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-2xl text-slate-50 flex items-center gap-2">
                  <Megaphone className="w-6 h-6 text-red-400" /> Formulario de denuncia
                </CardTitle>
                <p className="text-sm text-slate-400 mt-2">Todos los campos marcados con <span className="text-red-400">*</span> son obligatorios. Mientras más detalle aportes, más rápido podremos verificar.</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={submit} className="space-y-5" data-testid="denuncia-form">
                  {/* Datos personales */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Tu nombre <span className="text-red-400">*</span></Label>
                      <Input data-testid="denuncia-input-nombre" value={form.nombre} onChange={function (e) { update('nombre', e.target.value); }} placeholder="Nombre completo o seudónimo" className="bg-slate-950 border-slate-700 text-slate-100 mt-1.5" />
                    </div>
                    <div>
                      <Label className="text-slate-300">Email <span className="text-red-400">*</span></Label>
                      <Input data-testid="denuncia-input-email" type="email" value={form.email} onChange={function (e) { update('email', e.target.value); }} placeholder="tucorreo@ejemplo.cl" className="bg-slate-950 border-slate-700 text-slate-100 mt-1.5" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300 flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> Teléfono / WhatsApp</Label>
                      <Input data-testid="denuncia-input-telefono" value={form.telefono} onChange={function (e) { update('telefono', e.target.value); }} placeholder="+56 9 ..." className="bg-slate-950 border-slate-700 text-slate-100 mt-1.5" />
                    </div>
                    <div className="flex items-end pb-1">
                      <label className="flex items-start gap-2 text-xs text-slate-400 cursor-pointer">
                        <input type="checkbox" data-testid="denuncia-checkbox-autoriza" checked={form.autoriza_contacto} onChange={function (e) { update('autoriza_contacto', e.target.checked); }} className="mt-1 accent-emerald-500" />
                        <span>Autorizo a SinAdicciones.org a contactarme por email o WhatsApp para ampliar detalles.</span>
                      </label>
                    </div>
                  </div>

                  {/* Centro denunciado */}
                  <div className="border-t border-slate-800 pt-5">
                    <Label className="text-slate-300 flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> Centro o lugar denunciado <span className="text-red-400">*</span></Label>
                    <Input data-testid="denuncia-input-centro" value={form.centro} onChange={function (e) { update('centro', e.target.value); }} placeholder="Nombre del centro de rehabilitación" className="bg-slate-950 border-slate-700 text-slate-100 mt-1.5" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Ciudad</Label>
                      <Input data-testid="denuncia-input-ciudad" value={form.ciudad} onChange={function (e) { update('ciudad', e.target.value); }} placeholder="Ej: Santiago, Concepción, ..." className="bg-slate-950 border-slate-700 text-slate-100 mt-1.5" />
                    </div>
                    <div>
                      <Label className="text-slate-300">Región</Label>
                      <Input data-testid="denuncia-input-region" value={form.region} onChange={function (e) { update('region', e.target.value); }} placeholder="Ej: Metropolitana, Biobío, ..." className="bg-slate-950 border-slate-700 text-slate-100 mt-1.5" />
                    </div>
                  </div>

                  {/* Descripción */}
                  <div className="border-t border-slate-800 pt-5">
                    <Label className="text-slate-300">Describe la situación en detalle <span className="text-red-400">*</span></Label>
                    <p className="text-xs text-slate-500 mt-1 mb-1.5">Fechas, personas involucradas, qué pasó, cómo lo viviste. Mientras más detalle, mejor podemos verificar.</p>
                    <Textarea data-testid="denuncia-input-descripcion" value={form.descripcion} onChange={function (e) { update('descripcion', e.target.value); }} placeholder="Ejemplo: 'Ingresé al centro X en marzo 2025. Durante las primeras 2 semanas no me permitieron contactar a mi familia. El director me amenazó con prolongar mi internación si...'  " rows={8} className="bg-slate-950 border-slate-700 text-slate-100 mt-1.5 leading-relaxed" />
                    <p className="text-xs text-slate-500 mt-1">{form.descripcion.length} / 30 caracteres mínimos</p>
                  </div>

                  {/* Archivos */}
                  <div className="border-t border-slate-800 pt-5">
                    <Label className="text-slate-300 flex items-center gap-1"><Upload className="w-3.5 h-3.5" /> Adjuntar evidencias (opcional pero recomendado)</Label>
                    <p className="text-xs text-slate-500 mt-1">Fotos, videos o audios. Máx. {MAX_FILES} archivos · {MAX_FILE_MB} MB cada uno.</p>
                    <input ref={fileInputRef} type="file" accept={ACCEPT} multiple onChange={onPickFiles} className="hidden" id="denuncia-files" data-testid="denuncia-input-files" />
                    <label htmlFor="denuncia-files">
                      <Button type="button" asChild variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 cursor-pointer mt-2">
                        <span><Upload className="w-4 h-4 mr-2" />Seleccionar archivos ({files.length}/{MAX_FILES})</span>
                      </Button>
                    </label>
                    {files.length > 0 && (
                      <ul className="mt-3 space-y-1.5">
                        {files.map(function (f, i) {
                          const sizeKB = Math.round(f.size / 1024);
                          const Icon = f.type.startsWith('image/') ? Camera : f.type.startsWith('video/') ? Video : f.type.startsWith('audio/') ? Mic : FileText;
                          return (
                            <li key={i} className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm">
                              <div className="flex items-center gap-2 text-slate-200 truncate">
                                <Icon className="w-4 h-4 text-emerald-400 shrink-0" />
                                <span className="truncate">{f.name}</span>
                                <span className="text-xs text-slate-500 shrink-0">· {sizeKB} KB</span>
                              </div>
                              <button type="button" onClick={function () { removeFile(i); }} className="text-slate-500 hover:text-red-400 ml-2" aria-label="Quitar"><Trash2 className="w-4 h-4" /></button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>

                  {/* Confidencialidad */}
                  <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-xl p-4 flex items-start gap-3">
                    <Lock className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                    <div className="text-xs text-emerald-100/90 leading-relaxed">
                      <b className="text-emerald-200">Tu identidad permanece confidencial.</b> Tu nombre, email y teléfono nunca se publican. Solo el equipo editorial accede a tu denuncia. Si decidimos publicar el caso verificado, lo hacemos protegiendo tus datos personales.
                    </div>
                  </div>

                  <Button type="submit" disabled={submitting} data-testid="denuncia-submit-btn" className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-6 text-base">
                    {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando…</> : 'Enviar denuncia confidencial'}
                  </Button>
                  <p className="text-[11px] text-slate-500 text-center">Si fuiste víctima de un delito, denúncialo además en Fiscalía (8000 800 432) o Carabineros (133).</p>
                </form>
              </CardContent>
            </Card>
          )}
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-4 py-16" data-testid="denuncias-faq">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-50 mb-2 text-center">Preguntas frecuentes</h2>
          <p className="text-slate-400 text-sm text-center mb-8">Sobre denuncias y reclamos contra centros de rehabilitación en Chile.</p>
          <div className="space-y-2">
            {FAQ.map(function (item, idx) {
              const open = openFaq === idx;
              return (
                <div key={idx} className={'border rounded-xl overflow-hidden transition-colors ' + (open ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-slate-800 bg-slate-900')}>
                  <button type="button" onClick={function () { setOpenFaq(open ? -1 : idx); }} className="w-full flex items-center justify-between px-4 py-3.5 text-left" data-testid={'denuncias-faq-q-' + idx}>
                    <span className={'text-sm font-semibold ' + (open ? 'text-emerald-200' : 'text-slate-100')}>{item.q}</span>
                    <ChevronDown className={'w-4 h-4 transition-transform ' + (open ? 'rotate-180 text-emerald-300' : 'text-slate-500')} />
                  </button>
                  {open && (
                    <div className="px-4 pb-4 text-sm text-slate-300 leading-relaxed border-t border-slate-800/60 pt-3">{item.a}</div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* MISION / SEO TAIL */}
        <section className="max-w-4xl mx-auto px-4 py-12 border-t border-slate-800">
          <h2 className="text-xl font-bold text-slate-100 mb-3">Por qué este canal de denuncias importa</h2>
          <p className="text-slate-400 text-sm leading-relaxed mb-3">
            En Chile, miles de personas y familias buscan ayuda en centros de rehabilitación de drogas y alcohol cada año. Pero existe una brecha enorme entre los centros verificados (registrados ante <b>SENDA</b> y la <b>SEREMI de Salud</b>) y aquellos que operan sin habilitación, con malas prácticas o derechamente vulnerando derechos humanos. Este canal de <b>denuncias y reclamos contra centros de rehabilitación</b> existe para visibilizar esos casos y presionar por una regulación efectiva.
          </p>
          <p className="text-slate-400 text-sm leading-relaxed mb-3">
            Denunciar no es traicionar. Es proteger a otras personas que podrían pasar por lo mismo. Tu testimonio puede activar inspecciones, revocar permisos y, sobre todo, ayudar a otras familias a tomar decisiones informadas antes de internar a un ser querido.
          </p>
          <p className="text-slate-400 text-sm leading-relaxed">
            ¿Tienes dudas antes de denunciar? Lee también nuestra <Link to="/blog/denuncias-malas-practicas-centros-rehabilitacion-chile" className="text-emerald-400 hover:underline">guía sobre malas prácticas en centros de rehabilitación</Link> o nuestra <Link to="/blog/que-preguntar-centro-rehabilitacion-antes-ingresar" className="text-emerald-400 hover:underline">guía de preguntas antes de ingresar a un centro</Link>.
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
}
